
import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, TrendingUp, CheckCircle, Users, Award } from 'lucide-react';
import TrustedBy from './TrustedBy';
import Footer from './Footer';

const Gateway = ({ onSelectBusiness, onSelectLoans }) => {
  const stats = [
    { value: '13+', label: 'Years in Business', icon: <Award className="w-6 h-6" />, color: 'text-blue-600' },
    { value: '10,000+', label: 'Clients Served', icon: <Users className="w-6 h-6" />, color: 'text-green-600' },
    { value: '90%', label: 'Client Return Rate', icon: <TrendingUp className="w-6 h-6" />, color: 'text-purple-600' },
    { value: '6', label: 'Dedicated Specialists', icon: <Users className="w-6 h-6" />, color: 'text-blue-600' }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4 md:py-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2 sm:space-x-4"
            >
              <img 
                src="/Esh-Ham Main - Edited.png" 
                alt="Esh-Ham Logo" 
                className="h-10 sm:h-12 md:h-16 w-auto object-contain"
              />
              <div className="hidden xs:block">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-black">Esh-Ham</h1>
              </div>
            </motion.div>

            <div className="hidden lg:block">
              <h2 className="text-4xl font-dancing text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 text-shadow-sm">
                More than a Decade in Business!
              </h2>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3 sm:space-x-6"
            >
              <a href="tel:+26461306369" className="flex items-center space-x-1 sm:space-x-2 text-black hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">+264 61 306 369</span>
              </a>
              <a href="mailto:info@eshham.com" className="hidden md:flex items-center space-x-2 text-black hover:text-blue-600 transition-colors">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">info@eshham.com</span>
              </a>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section with Service Selection */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden isolate">
        <div className="absolute inset-0 bg-gray-100 z-[-1]">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-purple-600 to-blue-600 opacity-15 rounded-full -translate-x-1/2 pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-600 to-blue-600 opacity-15 rounded-full translate-x-1/2 pointer-events-none"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 sm:mb-12 md:mb-16"
          >
            <h2 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl font-bold text-black mb-12">
              What brings you to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Esh-ham</span>?
            </h2>
          </motion.div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Business Consultants Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onClick={onSelectBusiness}
              className="bg-white rounded-2xl shadow-xl p-8 cursor-pointer transform hover:scale-105 transition-transform duration-300 flex flex-col"
            >
              <div className="text-center flex-grow flex flex-col">
                <img 
                  src="/business-icon-logo.jpeg" 
                  alt="Business Consultants Logo" 
                  className="h-20 w-20 object-contain mx-auto mb-6"
                />
                <h3 className="text-3xl font-bold text-black mb-4">Business Consultants</h3>
                <p className="text-gray-700 mb-6 text-lg">
                  Offers company secretarial services.
                </p>
                <ul className="text-left space-y-3 mb-8 flex-grow">
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    New Business Registration
                  </li>
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    Company statutory requirements & compliancy
                  </li>
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    Business Coaching
                  </li>
                </ul>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectBusiness();
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg mt-auto"
                >
                  Enter Business Portal
                </button>
              </div>
            </motion.div>

            {/* Cash Loans Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              onClick={onSelectLoans}
              className="bg-white rounded-2xl shadow-xl p-8 cursor-pointer transform hover:scale-105 transition-transform duration-300 flex flex-col"
            >
              <div className="text-center flex-grow flex flex-col">
                <img 
                  src="/cash-loan-icon-logo2.jpeg" 
                  alt="Cash Loans Logo" 
                  className="h-20 w-20 object-contain mx-auto mb-6"
                />
                <h3 className="text-3xl font-bold text-black mb-4">Cash Loans</h3>
                <p className="text-gray-700 mb-6 text-lg">
                  Fast Personal Pay Day Loans
                </p>
                <ul className="text-left space-y-3 mb-8 flex-grow">
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    29% Interest Rate
                  </li>
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    Same Day Approval
                  </li>
                  <li className="flex items-center text-black">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                    Payout Within Hours
                  </li>
                </ul>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectLoans();
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg mt-auto"
                >
                  Apply for Loan
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent my-4"></div>

      {/* Corporate Overview Section - Who We Are */}
      <section className="py-20 bg-white isolate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Image at Top with Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mb-12"
            >
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="aspect-video rounded-2xl shadow-2xl overflow-hidden max-w-2xl mx-auto"
              >
                <img 
                  src="/Who We Are.jpg" 
                  alt="Esh-Ham Team" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-blue-600 rounded-full opacity-15 blur-3xl pointer-events-none z-[-1]"></div>
            </motion.div>

            {/* Text Content Below */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Who We Are
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At Esh-Ham Business Consultants, our philosophy is defined by precision, efficiency, and results. We strengthen the economy by delivering unwavering excellence to every client we serve.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Wholly Namibian-owned, Esh-Ham is a powerhouse of six dedicated specialists committed to the sustainable growth of our country's SME sector. We don't just help you start a business—we provide the strategic execution you need to deliver results.
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                  >
                    <div className={`w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 ${stat.color}`}>
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-gray-600 text-sm font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent my-4"></div>

      {/* Leadership Section - Ester Hamukoto */}
      <section className="py-20 bg-slate-50 isolate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Leadership
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leader behind Esh-Ham's success
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8 items-start">
              {/* Photo - Left (2 columns) */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative md:col-span-2"
              >
                <motion.div
                  whileHover={{ scale: 1.03, rotate: 1 }}
                  transition={{ duration: 0.3 }}
                  className="aspect-[3/4] bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-white"
                >
                  <img 
                    src="/ester-profile.jpeg" 
                    alt="Ester Hamukoto - Founder & CEO" 
                    className="w-full h-full object-cover object-center"
                  />
                </motion.div>
                {/* Decorative Elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-purple-400 rounded-full opacity-15 blur-2xl pointer-events-none z-[-1]"></div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-blue-600 rounded-full opacity-15 blur-2xl pointer-events-none z-[-1]"></div>
              </motion.div>

              {/* Bio - Right (3 columns) */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6 md:col-span-3"
              >
                <div>
                  <h4 className="text-3xl font-bold text-gray-900 mb-2">Ester Hamukoto</h4>
                  <p className="text-xl text-blue-600 font-semibold mb-6">Founder & CEO</p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  Ester Hamukoto is the founder of Esh-Ham Business Consultants who has been successfully managing the company for over 13 years. She holds a Master's degree in Business Administration and is currently completing her Doctor of Business Administration (PhD) at the Namibia Business School (NBS), an autonomous institution under the University of Namibia (UNAM).
                </p>

                <p className="text-gray-700 leading-relaxed">
                  Ester Hamukoto is a qualified consultant with more than 13 years in the SME consulting industry. She was recognized as an emerging entrepreneur by Namibia Business Hall of Fame in 2016 and has received various awards in her business career.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  For over 13 years, she has transformed Esh-Ham into a well-known business consulting firm with a strong base of both local and international clients.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <TrustedBy />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Gateway;
